import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { ArrowRight, Loader2, MessageSquare } from 'lucide-react';
import * as sandboxApi from '@/api/sandbox';
import type { SandboxStatus } from '@/api/sandbox';

const TFX_AUTH_URL = import.meta.env.VITE_TFX_AUTH_URL || 'https://auth.int.tubi.io';
const FONT_FAMILY = "'Aeonik Fono', 'Inter', system-ui, sans-serif";
const SLACK_FEEDBACK_URL = 'https://tubi.enterprise.slack.com/archives/C0A6EGKEUK1';

/** Portal-only routes that should NOT redirect to the sandbox */
const PORTAL_ROUTES = ['/settings', '/downloads', '/admin'];

/** Statuses that mean the sandbox is actively being set up */
const INITIALIZING_STATUSES: SandboxStatus[] = ['pending', 'creating', 'restarting'];

function TdaIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" fill="#FFDE00" rx="4" />
      <text x="16" y="22" fontFamily="system-ui" fontSize="18" fontWeight="bold" textAnchor="middle" fill="#000">T</text>
    </svg>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const clearedStaleAuth = useRef(false);

  const [sandboxStatus, setSandboxStatus] = useState<SandboxStatus | null>(null);
  const [sandboxUrl, setSandboxUrl] = useState<string | null>(null);
  const [checkingSandbox, setCheckingSandbox] = useState(false);

  const extAuthzRedirect = searchParams.get('redirect');

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
  const returnTo = from && PORTAL_ROUTES.some((r) => from.startsWith(r)) ? from : undefined;

  useEffect(() => {
    if (extAuthzRedirect && isAuthenticated && !clearedStaleAuth.current) {
      clearedStaleAuth.current = true;
      useAuthStore.getState().clearAuth();
    }
  }, [extAuthzRedirect, isAuthenticated]);

  const isOnLoginPath = location.pathname === '/login';

  // Authenticated user on /login → redirect to /landing
  // Unauthenticated user on /landing → redirect to /login
  useEffect(() => {
    if (extAuthzRedirect) return;
    if (isAuthenticated && isOnLoginPath) {
      navigate(returnTo ?? '/landing', { replace: true });
    } else if (!isAuthenticated && !isOnLoginPath) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isOnLoginPath, navigate, returnTo, extAuthzRedirect]);

  // When authenticated, check sandbox status
  const fetchSandboxStatus = useCallback(async () => {
    setCheckingSandbox(true);
    try {
      const response = await sandboxApi.getSandboxStatus();
      setSandboxStatus(response.status);
      setSandboxUrl(response.sandboxUrl);

      // Auto-redirect only when sandbox is actively initializing
      if (INITIALIZING_STATUSES.includes(response.status)) {
        navigate('/sandbox-loading', { replace: true });
      }
    } catch {
      setSandboxStatus('none');
    } finally {
      setCheckingSandbox(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated && !extAuthzRedirect && !returnTo) {
      fetchSandboxStatus();
    }
  }, [isAuthenticated, extAuthzRedirect, returnTo, fetchSandboxStatus]);

  const handleLogin = () => {
    // Persist returnTo across the external OIDC redirect so the callback
    // can navigate back to the intended destination (e.g. /admin/login).
    if (returnTo) {
      sessionStorage.setItem('tda_return_to', returnTo);
    } else {
      sessionStorage.removeItem('tda_return_to');
    }

    const params = new URLSearchParams({
      app_id: 'tda-portal',
      redirect_url: window.location.origin + '/auth/callback',
    });
    window.location.href = `${TFX_AUTH_URL}/auth/web/login?${params.toString()}`;
  };

  const handleSandboxAction = async () => {
    if (sandboxStatus === 'ready' && sandboxUrl) {
      window.location.href = sandboxUrl;
    } else {
      navigate('/sandbox-loading');
    }
  };

  // Not authenticated: show only the login screen
  if (!isAuthenticated || extAuthzRedirect) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 bg-white"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="flex-1 flex flex-col items-center justify-center">
          <h1
            className="text-4xl text-[var(--md-graphite)] mb-3"
            style={{ fontFamily: FONT_FAMILY, fontWeight: 200 }}
          >
            TDA Portal
          </h1>
          <p className="text-base text-[var(--md-slate)] mb-10" style={{ fontFamily: FONT_FAMILY }}>
            Sign in to access your development sandbox
          </p>

          <button
            type="button"
            onClick={handleLogin}
            className="w-full max-w-xs inline-flex items-center justify-center rounded-full border-0 bg-[var(--md-sunbeam)] px-6 py-3 text-base font-medium text-[var(--md-graphite)] transition-all hover:bg-[var(--md-sunbeam-dark)] hover:shadow-sm cursor-pointer"
            style={{ fontFamily: FONT_FAMILY }}
          >
            Sign in with Okta
          </button>

          <p className="text-xs text-[var(--md-slate)] mt-4" style={{ fontFamily: FONT_FAMILY }}>
            Only Tubi employees can access this portal.
          </p>
        </div>
      </div>
    );
  }

  // Authenticated: show two-column layout
  const sandboxButtonLabel =
    checkingSandbox
      ? 'Checking sandbox...'
      : sandboxStatus === 'ready'
        ? 'Launch your sandbox'
        : 'Initialize your sandbox';

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 bg-white"
      style={{ fontFamily: FONT_FAMILY }}
    >
      <div className="flex-1 flex items-center justify-center w-full">
      <div className="flex flex-col md:flex-row items-stretch w-full max-w-4xl md:gap-16">
        {/* Left: Sandbox action (primary) */}
        <div className="flex-1 flex flex-col items-center justify-center py-12 px-8">
          <h1
            className="text-4xl text-[var(--md-graphite)] mb-3"
            style={{ fontFamily: FONT_FAMILY, fontWeight: 200 }}
          >
            TDA Portal
          </h1>
          <p className="text-base text-[var(--md-slate)] mb-10" style={{ fontFamily: FONT_FAMILY }}>
            {sandboxStatus === 'ready' ? 'Welcome back! Your sandbox is ready to go' : 'Your development sandbox is one click away'}
          </p>

          <button
            type="button"
            onClick={handleSandboxAction}
            disabled={checkingSandbox}
            className={`w-full max-w-xs inline-flex items-center justify-center rounded-full border-0 px-6 py-3 text-base font-medium transition-all hover:shadow-sm cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${sandboxStatus === 'ready' ? 'bg-green-400 text-white hover:bg-green-500' : 'bg-[var(--md-sunbeam)] text-[var(--md-graphite)] hover:bg-[var(--md-sunbeam-dark)]'}`}
            style={{ fontFamily: FONT_FAMILY }}
          >
            {checkingSandbox && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {sandboxButtonLabel}
          </button>
        </div>

        {/* Vertical divider */}
        <div className="hidden md:block w-px bg-gray-200 self-stretch my-8" />
        {/* Horizontal divider on mobile */}
        <div className="md:hidden h-px bg-gray-200 mx-8" />

        {/* Right: Desktop app download (secondary) */}
        <div className="md:w-60 shrink-0 flex flex-col justify-center py-12 px-8 md:px-0 relative max-w-sm mx-auto md:mx-0">
          <TdaIcon className="w-10 h-10 mb-4" />
          <div className="relative w-fit mb-2">
            <h2
              className="text-base font-medium text-[var(--md-ink)] normal-case tracking-normal"
              style={{ fontFamily: FONT_FAMILY }}
            >
              TDA for macOS
            </h2>
            {/* Tape sticker label */}
            <div
              className="absolute -rotate-6"
              style={{ bottom: 'calc(100% - 4px)', left: 'calc(100% - 12px)', transformOrigin: 'bottom left' }}
            >
              <div
                className="px-2 py-px text-[8px] tracking-wider uppercase text-[var(--md-graphite)]"
                style={{ background: 'rgba(255, 222, 0, 0.4)', fontFamily: FONT_FAMILY }}
              >
                recommended
              </div>
            </div>
          </div>
          <p
            className="text-sm text-[var(--md-slate)] leading-snug mb-6"
            style={{ fontFamily: FONT_FAMILY }}
          >
            A macOS desktop app that runs locally. Orchestrate AI workflows, manage worktrees,
            and ship from requirements to PR.
          </p>

          <Link
            to="/downloads"
            className="inline-flex items-center gap-2 self-start rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-[var(--md-ink)] hover:bg-[var(--md-fog)] hover:shadow-sm transition-all"
            style={{ fontFamily: FONT_FAMILY }}
          >
            Visit download page
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
      </div>

      <a
        href={SLACK_FEEDBACK_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-[var(--md-slate)] hover:text-[var(--md-graphite)] transition-colors pb-6"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <MessageSquare className="w-3.5 h-3.5" />
        Feedback
      </a>
    </div>
  );
}
